// useRealtime — single shared Socket.IO connection. The browser sends the
// existing pr_access cookie automatically (withCredentials), which is what
// the backend realtime service reads to authenticate.
//
// Usage:
//   const socket = useRealtime();
//   useEffect(() => {
//     if (!socket) return undefined;
//     const h = (payload) => { ... };
//     socket.on('notification', h);
//     return () => socket.off('notification', h);
//   }, [socket]);
//
// The connection survives across pages — we cache a single client outside
// the React lifecycle so SPA navigation doesn't reconnect repeatedly.

import { useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuthStore } from '../store/authStore';

let cachedSocket = null;

const getApiBase = () => {
  const base = process.env.REACT_APP_BACKEND_URL
    || (typeof window !== 'undefined' ? window.location.origin : '');
  return base.replace(/\/api\/?$/, '');
};

export default function useRealtime() {
  const user = useAuthStore((s) => s.user);
  const [socket, setSocket] = useState(cachedSocket);

  useEffect(() => {
    if (!user) {
      if (cachedSocket) { try { cachedSocket.disconnect(); } catch (_) { /* ignore */ } cachedSocket = null; }
      setSocket(null);
      return undefined;
    }
    if (cachedSocket && cachedSocket.connected) {
      setSocket(cachedSocket);
      return undefined;
    }
    if (cachedSocket) { try { cachedSocket.disconnect(); } catch (_) { /* ignore */ } }

    const s = io(getApiBase(), {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnectionDelayMax: 10000,
    });
    cachedSocket = s;
    setSocket(s);
    return undefined;
    // We only react to user.id changes (login / logout); ignoring other
    // user mutations is intentional — the socket stays put as long as the
    // same user is logged in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return socket;
}
